/**
 * Token Service
 *
 * Centralized service for managing access tokens.
 * Wraps Auth0's getAccessTokenSilently to provide:
 * - Automatic logout on fatal authentication errors
 * - Prevention of retry loops after fatal errors
 */

import { captureException } from '@sentry/react';
import { authLog } from 'src/utils/logger';

type GetAccessTokenSilently = () => Promise<string>;
type LogoutFunction = () => void;

let tokenGetter: GetAccessTokenSilently | null = null;
let logoutHandler: LogoutFunction | null = null;
let hasFatalError = false;
let isLoggingOut = false;

/**
 * Initialize the token service with Auth0's getAccessTokenSilently function
 */
export const initializeTokenService = (getter: GetAccessTokenSilently, logout?: LogoutFunction): void => {
  tokenGetter = getter;
  logoutHandler = logout || null;
  hasFatalError = false;
  isLoggingOut = false;
};

export const clearTokenService = (): void => {
  tokenGetter = null;
  logoutHandler = null;
  hasFatalError = false;
  isLoggingOut = false;
};

export const isTokenServiceInitialized = (): boolean => {
  return tokenGetter !== null;
};

export const hasTokenServiceFatalError = (): boolean => {
  return hasFatalError;
};

const isFatalAuthError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;

  const message = error.message.toLowerCase();
  const fatalPatterns = [
    'missing refresh token',
    'login required',
    'invalid_grant',
    'invalid_token',
    'consent required',
    'interaction required',
    'access denied',
    'unauthorized',
  ];

  if (fatalPatterns.some((pattern) => message.includes(pattern))) {
    return true;
  }

  const errorAny = error as { status?: number; statusCode?: number; error?: string };
  if (errorAny.status === 403 || errorAny.status === 401) {
    return true;
  }
  if (errorAny.statusCode === 403 || errorAny.statusCode === 401) {
    return true;
  }

  if (errorAny.error === 'invalid_grant' || errorAny.error === 'access_denied') {
    return true;
  }

  return false;
};

/**
 * Trigger logout (with debounce to prevent multiple calls)
 */
const triggerLogout = (): void => {
  if (isLoggingOut || !logoutHandler) return;

  isLoggingOut = true;
  hasFatalError = true;

  setTimeout(() => {
    logoutHandler?.();
  }, 50);
};

export const getAccessToken = async (): Promise<string | null> => {
  // If we've already had a fatal error or are logging out, don't keep trying
  if (hasFatalError || isLoggingOut) {
    return null;
  }

  if (!tokenGetter) {
    return null;
  }

  try {
    return await tokenGetter();
  } catch (error) {
    if (isFatalAuthError(error)) {
      authLog.error('authentication failed, logging out', error);
      // Report fatal auth failures (involuntary logouts) so a regression that mass-
      // logs-out users is visible/alertable. Transient/non-fatal failures stay silent
      // to avoid noise. Only the Auth0 error code/status is attached — never the raw
      // error (which can carry verbose descriptions).
      const authErr = error as { error?: string; status?: number; statusCode?: number };
      captureException(new Error('Fatal auth token failure'), {
        tags: { context: 'auth_token', fatal: 'true' },
        fingerprint: ['auth_token_fatal', authErr.error ?? 'unknown'],
        extra: { code: authErr.error, status: authErr.status ?? authErr.statusCode },
      });
      triggerLogout();
    }
    return null;
  }
};
